import { reloadOnceForStaleDeploy } from 'lib/tracking/helpers/reloadOnceForStaleDeploy/reloadOnceForStaleDeploy'
import { isHardNavigateSameUrlError } from 'lib/tracking/helpers/isHardNavigateSameUrlError/isHardNavigateSameUrlError'

interface RecoverFromStaleHardNavigationParams {
  error: unknown
}

const STALE_HARD_NAV_RELOAD_GUARD_KEY = 'stale-hard-nav-reload-attempted-at'

const toMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error))

/**
 * Recovers from Next's stale-deploy "hard navigate to the same URL" invariant (see
 * `isHardNavigateSameUrlError`) by reloading the page once so the browser re-fetches the fresh build
 * instead of leaving the user on a tab whose next navigation silently fails until they retry.
 *
 * Returns whether the error was that invariant — i.e. whether this helper took ownership of it. The
 * caller uses that to mark the event handled; it must NOT log the error, which is benign deploy noise
 * already dropped from RUM by the `beforeSend` filter. When a reload can't safely fire (a repeat
 * within the guard window, an offline tab, SSR, or unavailable storage) the error is claimed silently
 * with no reload. See `reloadOnceForStaleDeploy` for the guard state machine.
 */
export const recoverFromStaleHardNavigation = ({ error }: RecoverFromStaleHardNavigationParams): boolean => {
  if (!isHardNavigateSameUrlError({ message: toMessage(error) })) {
    return false
  }

  reloadOnceForStaleDeploy({
    guardKey: STALE_HARD_NAV_RELOAD_GUARD_KEY,
    action: 'stale_hard_navigation_reload',
  })

  return true
}
